import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { finalize, map, Observable, of, shareReplay, tap } from 'rxjs';
import { ENVIRONMENT } from '../../../const';
import { CreateSubtenantPayload, SubtenantsResponse, UpdateSubtenantPayload } from '../models/insegnamento.model';

const SUBTENANT_TOKEN_KEY = 'subtenant_token';
const SELECTED_TEACHING_ID_KEY = 'selectedTeachingId';

@Injectable({ providedIn: 'root' })
export class SubtenantService {

    private readonly endpoint: string;

    private subtenantTokenRefresh$: Observable<string | null> | null = null;
    private loggingOut = false;

    constructor(
        @Inject(ENVIRONMENT) environment: any,
        private http: HttpClient
    ) {
        this.endpoint = environment.endpoint;
    }

    getSubtenantToken(): string | null {
        return sessionStorage.getItem(SUBTENANT_TOKEN_KEY);
    }

    getValidSubtenantToken(): Observable<string | null> {
        const token = this.getSubtenantToken();
        if (!token) {
            return of(null);
        }
        if (this.isTokenExpired(token)) {
            return this.refreshSubtenantToken();
        }
        return of(token);
    }

    private isTokenExpired(token: string, offsetSeconds = 30): boolean {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const expiry = payload.exp * 1000;
            return expiry - offsetSeconds * 1000 < Date.now();
        } catch {
            return true;
        }
    }

    createSubtenant(payload: CreateSubtenantPayload): Observable<SubtenantsResponse> {
        return this.http.post<SubtenantsResponse>(
            `${this.endpoint}/user/subtenants/create`,
            payload
        );
    }

    getSubtenants(): Observable<SubtenantsResponse[]> {
        return this.http.get<SubtenantsResponse[]>(`${this.endpoint}/user/subtenants`);
    }

    updateSubtenant(payload: UpdateSubtenantPayload): Observable<SubtenantsResponse> {
        return this.http.put<SubtenantsResponse>(
            `${this.endpoint}/user/subtenants/update`,
            payload
        );
    }

    deleteSubtenant(id: string | number): Observable<unknown> {
        return this.http.delete(
            `${this.endpoint}/user/subtenants/delete`,
            { body: { id: String(id) } }
        );
    }

    selectSubtenant(subtenantId: string | number): Observable<string> {
        return this.http
            .post(
                `${this.endpoint}/user/select-subtenant`,
                { subtenantId: String(subtenantId) },
                { responseType: 'text' }
            )
            .pipe(
                map((response: string) => this.extractToken(response)),
                tap((token: string) => {
                    if (!this.loggingOut) {
                        sessionStorage.setItem(SUBTENANT_TOKEN_KEY, token);
                    }
                })
            );
    }

    refreshSubtenantToken(): Observable<string | null> {
        if (this.loggingOut) {
            return of(null);
        }
        if (this.subtenantTokenRefresh$) {
            return this.subtenantTokenRefresh$;
        }

        const id = sessionStorage.getItem(SELECTED_TEACHING_ID_KEY);
        if (!id) {
            return of(null);
        }

        this.subtenantTokenRefresh$ = this.selectSubtenant(id).pipe(
            finalize(() => { this.subtenantTokenRefresh$ = null; }),
            shareReplay(1)
        );
        return this.subtenantTokenRefresh$;
    }

    clearSubtenantToken(): void {
        sessionStorage.removeItem(SUBTENANT_TOKEN_KEY);
    }
    
    beginLogout(): void {
        this.loggingOut = true;
        this.subtenantTokenRefresh$ = null;
        sessionStorage.removeItem(SUBTENANT_TOKEN_KEY);
        sessionStorage.removeItem(SELECTED_TEACHING_ID_KEY);
    }

    private extractToken(response: string): string {
        try {
            const parsed = JSON.parse(response);
            if (parsed && typeof parsed.token === 'string') {
                return parsed.token;
            }
        } catch {
            // response is already the raw token string
        }
        return response;
    }
}
